import { useEffect, useId, useRef, useState } from 'react'
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded'
import Popper from '@mui/material/Popper'
import styled from 'styled-components'

import { getServiceMoment, isBeforeServiceDayStart } from '../../utils/serviceDate'

const SERVICE_DAY_DESCRIPTION = 'akive의 하루는 오전 5시에 시작해 다음 날 오전 4시 59분에 끝나요. 오전 5시 전의 활동은 전날 학습일에 포함돼요.'

const Root = styled.div`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
`

const HelpButton = styled.button`
  display: inline-grid;
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  padding: 0;
  place-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;

  svg { width: 15px; height: 15px; }
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primaryAccent};
    background: ${({ theme }) => theme.colors.primarySoft};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

const HelpPopper = styled(Popper)`
  z-index: 130;
`

const HelpBubble = styled.span`
  display: grid;
  width: min(380px, calc(100vw - 40px));
  gap: 7px;
  margin: 0;
  padding: 13px 15px;
  color: ${({ theme }) => theme.colors.text} !important;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.medium};
  font-size: 0.78rem !important;
  font-style: normal;
  font-weight: 500 !important;
  line-height: 1.6;
  letter-spacing: normal;
  text-align: left;
  white-space: normal;
  word-break: keep-all;
`

const CurrentServiceDay = styled.em`
  color: ${({ theme }) => theme.colors.primaryAccent};
  font-style: normal;
  font-weight: 700;
`

export default function ServiceDayHelp({
  align = 'left',
  extraDescription = '',
  label = 'akive 서비스 시간 안내',
  placement = 'bottom',
  showCurrentServiceDay = false,
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const bubbleRef = useRef(null)
  const generatedId = useId()
  const tooltipId = `service-day-help-${generatedId.replace(/:/g, '')}`
  const isCarryoverTime = showCurrentServiceDay && isBeforeServiceDayStart()
  const currentServiceDay = getServiceMoment().format('M월 D일')

  useEffect(() => {
    if (!open) return undefined

    const closeOnOutsidePress = (event) => {
      if (!rootRef.current?.contains(event.target) && !bubbleRef.current?.contains(event.target)) {
        setOpen(false)
      }
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', closeOnOutsidePress)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  return (
    <Root ref={rootRef}>
      <HelpButton
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={tooltipId}
        onClick={() => setOpen((current) => !current)}
      >
        <QuestionMarkRoundedIcon aria-hidden="true" />
      </HelpButton>
      {open && (
        <HelpPopper
          id={tooltipId}
          open
          anchorEl={rootRef.current}
          placement={`${placement}-${align === 'right' ? 'end' : 'start'}`}
          modifiers={[
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'preventOverflow', options: { padding: 12 } },
            { name: 'flip', options: { padding: 12 } },
          ]}
        >
          <HelpBubble ref={bubbleRef}>
            {SERVICE_DAY_DESCRIPTION}{extraDescription ? ` ${extraDescription}` : ''}
            {isCarryoverTime && (
              <CurrentServiceDay>현재 활동은 {currentServiceDay} 학습일에 기록돼요.</CurrentServiceDay>
            )}
          </HelpBubble>
        </HelpPopper>
      )}
    </Root>
  )
}
